/**
 * createMarketingAnalytics — consent-gated event emitter for marketing
 * surfaces.
 *
 * Wraps a consumer-supplied transport adapter (typically a thin wrapper
 * around `@puffin-core/analytics`'s EventQueue) and decorates every
 * outgoing event with:
 *   - The canonical namespace prefix `marketing.<appKey>.<eventName>`.
 *   - First-touch + last-touch UTM attribution (read via utmCapture).
 *   - The current locale (when supplied).
 *   - A monotonic visitor session id (held in storage, regenerated when
 *     the visitor's session expires per the configured TTL).
 *
 * Consent gating: the factory is given a `getConsent()` callback. When
 * consent is false, `track()` returns immediately without invoking the
 * transport. The factory ALSO exposes a `flushQueuedOnConsent()` so a
 * consumer can opt to buffer pre-consent events and flush on accept.
 *
 * @module @puffin-core/marketing-shell/createMarketingAnalytics
 */
/* global process */

import { isConversionEvent, buildEventKey } from '../analytics/conversionEvents.js';
import {
  captureAttribution,
  readAttribution,
  DEFAULT_STORAGE_KEY as DEFAULT_ATTRIBUTION_KEY,
} from '../analytics/utmCapture.js';

const DEFAULT_SESSION_TTL_MS = 30 * 60 * 1000; // 30 min — matches GA default.
const DEFAULT_SESSION_STORAGE_KEY = 'marketingAnalyticsSession';

/**
 * Default cap on the pre-consent buffer (Phase 28 / E16).
 *
 * The marketing visitor session typically fires 5–20 events before the
 * cookie banner decision arrives, so a 1000-event ceiling gives ~50×
 * headroom while still capping unbounded growth in pathological cases
 * (eg. bot traffic, automated scrapers, cookie-banner refusal in a long
 * SPA-navigation session).
 *
 * Configurable per consumer via `config.bufferLimit`.
 */
const DEFAULT_PRECONSENT_BUFFER_LIMIT = 1000;

/**
 * @typedef {Object} TransportAdapter
 * @property {(envelope: {name: string, properties: Object}) => void} track
 *   Receives the final namespaced envelope (after UTM + locale enrichment).
 */

/**
 * @typedef {Object} StorageAdapter
 * @property {(key: string) => string|null} get
 * @property {(key: string, value: string) => void} set
 */

/**
 * @typedef {Object} CreateMarketingAnalyticsConfig
 * @property {string} appKey
 * @property {TransportAdapter} transport
 * @property {StorageAdapter} storageAdapter
 * @property {() => boolean} getConsent          Read the current consent state synchronously.
 * @property {() => string} [getLocale]          Return current locale to stamp on each event.
 * @property {() => string} [getUrl]             Return current URL (for UTM capture). Defaults to window.location.href.
 * @property {boolean} [bufferPreConsent=true]   When true, events fired before consent is granted are buffered + flushed on grant.
 * @property {number} [bufferLimit=1000]         Cap on the pre-consent buffer; FIFO eviction once exceeded (Phase 28 / E16).
 * @property {{warn?: Function, error?: Function}} [logger] Optional logger DI seam — receives one-time overflow warnings.
 * @property {number} [sessionTtlMs=30*60*1000]
 * @property {string} [attributionStorageKey='marketingAttribution']
 * @property {string} [sessionStorageKey='marketingAnalyticsSession']
 * @property {() => Date} [now]
 * @property {() => string} [sessionIdFactory]   Defaults to crypto.randomUUID (when available).
 */

/**
 * @typedef {Object} BufferStats
 * @property {number} size                        Current buffered-event count (always ≤ limit).
 * @property {number} limit                       Configured FIFO cap.
 * @property {number} droppedCount                Cumulative number of FIFO-evicted events since creation.
 * @property {boolean} overflowWarned             Whether the one-time overflow warning has fired.
 */

/**
 * @typedef {Object} MarketingAnalyticsApi
 * @property {(eventName: string, properties?: Object) => void} track
 * @property {() => void} flushQueuedOnConsent
 * @property {() => AttributionRecord|null} getAttribution
 * @property {() => string} ensureSession         Returns the current session id (creates one if missing/expired).
 * @property {() => void} reset                   Clears the pre-consent buffer + ends the active session.
 * @property {() => BufferStats} getBufferStats   Observability hook for the pre-consent buffer (Phase 28 / E16).
 */

/**
 * Build the analytics factory bound to the consumer's transport + storage.
 *
 * @param {CreateMarketingAnalyticsConfig} config
 * @returns {MarketingAnalyticsApi}
 */
export default function createMarketingAnalytics(config) {
  validateConfig(config);

  const {
    appKey,
    transport,
    storageAdapter,
    getConsent,
    getLocale,
    getUrl,
    bufferPreConsent = true,
    bufferLimit = DEFAULT_PRECONSENT_BUFFER_LIMIT,
    logger,
    sessionTtlMs = DEFAULT_SESSION_TTL_MS,
    attributionStorageKey = DEFAULT_ATTRIBUTION_KEY,
    sessionStorageKey = DEFAULT_SESSION_STORAGE_KEY,
    now = () => new Date(),
    sessionIdFactory,
  } = config;

  const resolvedBufferLimit =
    Number.isFinite(bufferLimit) && bufferLimit > 0
      ? Math.floor(bufferLimit)
      : DEFAULT_PRECONSENT_BUFFER_LIMIT;

  const queue = [];
  let droppedCount = 0;
  let overflowWarned = false;

  function warnOverflowOnce() {
    if (overflowWarned) return;
    overflowWarned = true;
    const message =
      '[marketingAnalytics] pre-consent buffer overflow; dropping oldest event (FIFO)';
    const ctx = { appKey, bufferLimit: resolvedBufferLimit };
    if (logger && typeof logger.warn === 'function') {
      logger.warn(message, ctx);
    } else if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn(message, ctx);
    }
  }

  function enqueuePreConsent(record) {
    while (queue.length >= resolvedBufferLimit) {
      queue.shift();
      droppedCount += 1;
      warnOverflowOnce();
    }
    queue.push(record);
  }

  function resolveUrl() {
    if (typeof getUrl === 'function') return getUrl();
    if (typeof window !== 'undefined' && window.location) return window.location.href;
    return null;
  }

  function makeSessionId() {
    if (typeof sessionIdFactory === 'function') return sessionIdFactory();
    if (
      typeof globalThis !== 'undefined' &&
      globalThis.crypto &&
      typeof globalThis.crypto.randomUUID === 'function'
    ) {
      return globalThis.crypto.randomUUID();
    }
    return `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }

  function ensureSession() {
    const stamp = now();
    // The storage adapter absorbs the browser refusing storage (`createLocalStorageAdapter`), so
    // `get` / `set` are not caught here: a throw from either is a defect.
    const raw = storageAdapter.get(sessionStorageKey);
    let snapshot = null;
    if (raw) {
      try {
        snapshot = JSON.parse(raw);
      } catch (error) {
        // A stored session that is not JSON is no session: a fresh one is minted below.
        if (!(error instanceof SyntaxError)) throw error;
      }
    }
    if (
      snapshot &&
      typeof snapshot.id === 'string' &&
      typeof snapshot.expiresAt === 'string' &&
      new Date(snapshot.expiresAt).getTime() > stamp.getTime()
    ) {
      // Slide the TTL forward on every event so the session stays alive
      // for the configured idle window.
      const next = {
        id: snapshot.id,
        expiresAt: new Date(stamp.getTime() + sessionTtlMs).toISOString(),
        startedAt: snapshot.startedAt,
      };
      writeSession(next);
      return snapshot.id;
    }
    const fresh = {
      id: makeSessionId(),
      startedAt: stamp.toISOString(),
      expiresAt: new Date(stamp.getTime() + sessionTtlMs).toISOString(),
    };
    writeSession(fresh);
    return fresh.id;
  }

  function writeSession(record) {
    storageAdapter.set(sessionStorageKey, JSON.stringify(record));
  }

  function getAttribution() {
    return readAttribution({ storageAdapter, storageKey: attributionStorageKey });
  }

  function buildEnvelope(eventName, properties) {
    const url = resolveUrl();
    // Refresh UTM capture on every track (covers landing-page UTM that
    // arrives mid-session, not just first page-load).
    if (url) {
      captureAttribution({
        url,
        storageAdapter,
        storageKey: attributionStorageKey,
        now,
      });
    }
    const attribution = getAttribution();
    const sessionId = ensureSession();
    const enriched = {
      ...(properties || {}),
      sessionId,
      occurredAt: now().toISOString(),
    };
    if (attribution) {
      enriched.attribution = attribution;
    }
    if (typeof getLocale === 'function') {
      const locale = getLocale();
      if (typeof locale === 'string' && locale) {
        enriched.locale = locale;
      }
    }
    return {
      name: buildEventKey(appKey, eventName),
      properties: enriched,
    };
  }

  // No catch: every transport this factory is given NEVER throws from `track()` — that is the
  // transport contract (`createHttpAnalyticsTransport` and `createGa4Transport` report a failed
  // send through `onError`; `composeTransports` isolates each destination and reports through
  // `onError` too). A throw that reaches here is therefore a defect in a transport, and it surfaces.
  function track(eventName, properties) {
    if (!isConversionEvent(eventName)) {
      // Unknown event names are a configuration error — fail loud in
      // development, no-op in production. Use `if (process.env.NODE_ENV...)`
      // sparingly; here it gates a hard throw to give consumers fast
      // feedback during dev.
      // NAV-ICON-COVERAGE-001 (2026-09-03) — this read
      // `typeof process !== 'undefined' && ...`, ALWAYS false in a browser, so
      // the "fail loud in development" throw never fired and a misconfigured
      // conversion-event name silently no-opped in dev. Two things make this
      // the clearest case of the three: the comment directly above ALREADY
      // prescribed the correct form (`if (process.env.NODE_ENV...)`), and this
      // file ALREADY carried the `/* global process */` declaration that makes
      // the bare literal lint clean. The broken form bought nothing.
      if (process.env.NODE_ENV !== 'production') {
        throw new TypeError(`marketingAnalytics.track: "${eventName}" is not in CONVERSION_EVENTS`);
      }
      return;
    }
    // `getConsent` is validated as a function by validateConfig, so no
    // typeof guard is needed here.
    const consent = Boolean(getConsent());
    if (!consent) {
      if (bufferPreConsent) {
        enqueuePreConsent({ eventName, properties });
      }
      return;
    }
    transport.track(buildEnvelope(eventName, properties));
  }

  function flushQueuedOnConsent() {
    if (queue.length === 0) return;
    // `getConsent` is validated as a function by validateConfig.
    const consent = Boolean(getConsent());
    if (!consent) return;
    while (queue.length > 0) {
      const next = queue.shift();
      transport.track(buildEnvelope(next.eventName, next.properties));
    }
  }

  function reset() {
    queue.length = 0;
    droppedCount = 0;
    overflowWarned = false;
    storageAdapter.set(sessionStorageKey, '');
  }

  function getBufferStats() {
    return {
      size: queue.length,
      limit: resolvedBufferLimit,
      droppedCount,
      overflowWarned,
    };
  }

  return {
    track,
    flushQueuedOnConsent,
    getAttribution,
    ensureSession,
    reset,
    getBufferStats,
  };
}

function validateConfig(config) {
  if (!config || typeof config !== 'object') {
    throw new TypeError('createMarketingAnalytics: config object is required');
  }
  if (typeof config.appKey !== 'string' || !config.appKey) {
    throw new TypeError('createMarketingAnalytics: config.appKey (string) is required');
  }
  if (!config.transport || typeof config.transport.track !== 'function') {
    throw new TypeError('createMarketingAnalytics: config.transport.track must be a function');
  }
  if (
    !config.storageAdapter ||
    typeof config.storageAdapter.get !== 'function' ||
    typeof config.storageAdapter.set !== 'function'
  ) {
    throw new TypeError(
      'createMarketingAnalytics: config.storageAdapter.{get,set} must be functions',
    );
  }
  if (typeof config.getConsent !== 'function') {
    throw new TypeError('createMarketingAnalytics: config.getConsent must be a function');
  }
}
